
<template>
  <article class="intl-live-rank" :class="{ expanded: isExpanded }" ref="wrapper">
    <header class="rank-header">
      <h2>{{ $t("rank.title") }}</h2>
    </header>
    <section class="rank-list-wrapper">
      <async-custom-scrollbar
        :style="{ height: listHeight }"
        :autoHideDelay="0"
        :autoHide="false"
        throttleType="throttle"
        ref="scroll"
      >
        <ul class="rank-list">
          <li v-show="isFirstLoading && !rankData.length" class="rank-warning">
            <img src="@/assets/rank-list/loading.png" />
            <p>Loading</p>
          </li>
          <li class="rank-warning" v-show="!isFirstLoading && !rankData.length">
            <img src="@/assets/rank-list/empty.png" />
            <p>{{ $t("rank.noData") }}</p>
          </li>
          <li
            v-for="(item, $index) in showedRankData"
            :key="item.mid"
            :class="{
              mine: item.mid === currentMid,
              [`rank-${$index + 1}`]: true,
            }"
          >
            <div :class="`rank-no`" :data-rank-no="`${$index + 1}`" />
            <a
              :href="`/${lang}/space/${item.mid}`"
              target="_blank"
              :style="{
                backgroundImage: `url(${item.face}@32w_32h_1e_1c_1f.webp)`,
              }"
              class="rank-avatar"
            />
            <a :href="`/${lang}/space/${item.mid}`" class="rank-username">
              {{ item.name }}
            </a>
            <div class="rank-score score">{{ item.score }}</div>
          </li>
        </ul>
      </async-custom-scrollbar>
    </section>
    <footer :class="`rank-footer rank-${myRank.rank}`" v-show="isExpanded">
      <template v-if="userInfo.mid">
        <template v-if="myRank.rank > 0">
          <div class="rank-no" :data-rank-no="myRank.rank"></div>
          <div
            class="rank-avatar"
            :style="{
              backgroundImage: `url(${userInfo.avatar}@32w_32h_1e_1c_1f.webp)`,
            }"
          ></div>
          <div class="my-score">
            {{ $t("rank.myScore") }}:
            <span class="score">{{ myRank.score }}</span>
          </div>
        </template>
        <template v-else>
          <div class="tip">{{ $t("rank.toContribute") }}</div>
        </template>
      </template>
      <template v-else>
        <div class="tip">
          <a class="login-link" @click="() => onLogin()">
            {{ $t("rank.login") }}
          </a>
          {{ $t("rank.viewRank") }}
        </div>
      </template>
    </footer>
  </article>
</template>


<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, toRefs, computed, defineAsyncComponent } from 'vue';
import { useStore } from 'vuex';
import LangManager from '@/locales/lang-manager';
import emitter, { EB_EVENT_NAME } from '@/utils/eventbus';
import { formatNumber } from '@/utils/format';
import RankService from '@/service/rank';

const props = defineProps<{
  onLogin: Function;
}>();

const store = useStore();

const AsyncCustomScrollbar =  defineAsyncComponent(() =>  import('custom-vue-scrollbar'));

const { onLogin } = toRefs(props);

// 数据处理 - 开始
const userInfo = computed(() => store.state.global.userInfo);
const currentMid = computed(() => parseInt(store.state.global?.userInfo?.mid || 0, 10));
const roomId = computed(() => store.state.room.roomInfo?.room_id);
const lang = computed(() => LangManager.get());

const rankData = ref([]);

const listHeight = computed(() => {
  const itemHeight = 40;
  const gapHeight = 5;
  return `${(isExpanded.value ? 8 : 3) * (itemHeight + gapHeight) - gapHeight}px`;
});

const myRank = computed(() => {
  const index = rankData.value.findIndex((item) => item.mid === parseInt(userInfo.value.mid, 10));
  return index > -1
    ? {
      rank: index + 1,
      score: rankData.value[index].score
    }
    : {
      rank: 0,
      score: 0
    };
});

const isFirstLoading = ref(true);

const switchTopUsers = (data = []) => {
  if (isExpanded.value) {
    return;
  }
  rankData.value = data.map((item) => ({
    ...item,
    face: item.face.replace(/^https?:/, ''),
    score: formatNumber(item.score || 0)
  }));
};

const getRankData = async () => {
  if (isExpanded.value) {
    return;
  }
  try{
    const resp = await RankService.getRankData(roomId.value);
    isFirstLoading.value = false;
    const rawData = resp.data?.user_ranks || [];
    rankData.value = rawData.map((item) => ({
      ...item,
      face: item.face.replace(/^https?:/, ''),
      score: formatNumber(item.score || 0)
    }));
    return rawData.length;
  }catch(e){
    console.log('**error(getRankData):',e);
  }
};

const showedRankData = computed(() => {
  return isExpanded.value ? rankData.value : rankData.value.slice(0, 3);
});
// 数据处理 - 结束

// 处理hover 事件 - 开始
const wrapper = ref(null);
const isExpanded = ref(false);
let hoverTimer = null;
let isEntered = false;

const startHover = () => {
  isEntered = true;
  if (hoverTimer) {
    clearTimeout(hoverTimer);
  }
  hoverTimer = setTimeout(async () => {
    const dataLength = await getRankData();
    if (isEntered && dataLength > 0) {
      isExpanded.value = true;
    }
    hoverTimer = null;
  }, 400);
};

const endHover = () => {
  isEntered = false;
  isExpanded.value = false;
};
// 处理hover 事件 - 结束

// 初始化事件绑定 - 开始
onMounted(async () => {
  await getRankData();
  wrapper.value?.addEventListener('mouseenter', startHover);
  wrapper.value?.addEventListener('mouseleave', endHover);
  emitter.on(EB_EVENT_NAME.RANK_TOP_USERS, switchTopUsers);
});

onBeforeUnmount(() => {
  wrapper?.value?.removeEventListener('mouseenter', startHover);
  wrapper?.value?.removeEventListener('mouseleave', endHover);
  emitter.off(EB_EVENT_NAME.RANK_TOP_USERS, switchTopUsers);
});
// 组件事件绑定 - 结束
</script>


<style lang="less" scoped>
@import '@/styles/polyfill/gap.less';

.intl-live-rank {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: absolute;
  line-height: 40px;
  min-height: 192px;
  font-size: 14px;
  color: #222;
  left: 0;
  top: 0;
  background: #f9f9f9;
  overflow: hidden;
  border-radius: 8px 8px 0 0;

  & > * {
    background: #fff;
  }

  .shadow(@deg, @top) {
    content: '';
    display: block;
    position: absolute;
    top: @top;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(@deg, rgba(26, 26, 26, 0.05), rgba(26, 26, 26, 0));
  }

  &::after {
    .shadow(180deg, calc(100% - 5px));

    position: static;
  }

  &.expanded {
    background: white;
    z-index: 1;
    border-radius: 8px 8px 0 0;
    transition: all 0.2s ease-in-out;
    height: 457px;

    .scrollbar__scroller {
      height: 355px;
    }

    .rank-footer {
      display: flex;
    }
  }

  .rank-header {
    font-size: 18px;
    padding: 12px;
    border-bottom: 1px solid rgba(238, 238, 238, 1);
    flex: 0 0 auto;
    background: #fff;
    border-radius: 8px 8px 0 0;
    flex: 0 0 auto;

    & > h2 {
      margin: 0;
      padding: 0;
      font-size: 18px;
      line-height: 22px;
    }
  }

  .rank-list-wrapper {
    flex-grow: 1;
    padding: 5px 0;

    .scrollbar__scroller {
      height: 130px;
    }

    ul.rank-list {
      list-style: none;
      .flex-gap(5px, 'column nowrap');

      display: flex;
      flex-direction: column;

      & > li {
        padding: 0 12px;
        height: 40px;
        display: flex;
        .flex-gap(8px);

        align-items: center;

        & > * {
          flex-grow: 0;
          flex-shrink: 0;
        }

        & > .rank-username {
          flex-grow: 1;
          flex-shrink: 1;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        &.rank-warning {
          text-align: center;
          margin: 7px 0;
          flex-direction: column;

          .flex-gap(0, 'column nowrap');

          height: 114px;

          & > img {
            height: 80px;
            display: block;
            margin: 0 auto;
          }

          & > p {
            flex: 0 0 auto;
            font-size: 12px;
            color: #999;
            text-align: center;
            line-height: 14px;
            margin: 8px 0 0;
          }
        }

        &:not(.rank-warning).mine {
          background: rgba(153, 153, 153, 0.1);
        }
      }
    }
  }

  .rank-footer {
    display: none;
    padding: 0 12px;
    .flex-gap(8px);

    flex-direction: row;
    align-items: center;
    flex: 0 0 40px;
    position: relative;
    color: #999;

    &::before {
      .shadow(0, -5px);
    }

    & > div:first-child:is(:last-child) {
      text-align: center;
      flex-grow: 1;
    }

    .tip {
      font-size: 12px;
    }
  }

  .rank-no {
    color: #999;
    flex: 0 0 25px;
    text-align: center;

    &[data-rank-no]::after {
      content: attr(data-rank-no);
      display: block;
    }
  }

  .rank-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    align-self: center;
    background-image: url('@/assets/noface.png');
    background-position: center;
    background-size: contain;
    position: relative;
  }

  .rank-score {
    font-size: 12px;
  }

  .score {
    color: #ffab00;
  }

  .login-link {
    color: #4c93ff;
    cursor: pointer;
  }

  .top-rank(@bgImage) {
    background-image: @bgImage;
    height: 25px;
    background-position: center;
    background-size: cover;

    &::after {
      display: none;
    }
  }

  .top-rank-crown(@bgImage) {
    &::after {
      content: '';
      display: block;
      background-image: @bgImage;
      width: 40px;
      height: 40px;
      position: absolute;
      left: -8px;
      top: -8px;
      background-position: center;
      background-size: cover;
    }
  }

  .rank-1 {
    .rank-no {
      .top-rank(url('@/assets/rank-list/rank_1.png'));
    }

    .rank-avatar {
      .top-rank-crown(url('@/assets/rank-list/rank_1_crown.png'));
    }
  }

  .rank-2 {
    .rank-no {
      .top-rank(url('@/assets/rank-list/rank_2.png'));
    }

    .rank-avatar {
      .top-rank-crown(url('@/assets/rank-list/rank_2_crown.png'));
    }
  }

  .rank-3 {
    .rank-no {
      .top-rank(url('@/assets/rank-list/rank_3.png'));
    }

    .rank-avatar {
      .top-rank-crown(url('@/assets/rank-list/rank_3_crown.png'));
    }
  }
}
</style>
